import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Chip, alpha, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import request from '../../services/Request';
import { formatBytes } from '../../services/method/methods';
import ReactApexChart from 'react-apexcharts';
import { CloudQueue, VerifiedUser, LockOpen, AdminPanelSettings } from '@mui/icons-material';

export default function ModernDeposits(props) {
  const theme = useTheme();
  const [dataused, setdataused] = useState(0);
  const [totalSpace, setTotalSpace] = useState(1000000000);
  const [data, setData] = useState([
    { name: 'Used', value: 0 },
    { name: 'Free', value: 1000000000 },
  ]);

  const user = JSON.parse(localStorage.getItem('user'));

  const ss = useMemo(
    () => ({
      series: [data[0].value, data[1].value],
      options: {
        chart: {
          width: 176,
          height: 176,
          type: 'donut',
        },
        labels: ['Used', 'Free'],
        colors: [theme.palette.primary.main, theme.palette.grey[300]],
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '11.2px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
                value: {
                  show: true,
                  fontSize: '19.2px',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  formatter: (val) => formatBytes(val),
                },
                total: {
                  show: true,
                  label: 'Used',
                  fontSize: '11.2px',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  formatter: () => formatBytes(data[0].value),
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        stroke: {
          width: 0,
        },
        tooltip: {
          y: {
            formatter: (val) => formatBytes(val),
          },
          theme: theme.palette.mode,
        },
      },
    }),
    [data, theme]
  );

  useEffect(() => {
    if (user?.role === 'SuperAdmin') return;
    const getUserData = async () => {
      await request({
        method: 'get',
        url: `/users/edit/${props.user?._id}`,
      }).then((res) => {
        const features = res.data?.features;
        if (res.data?.features) {
          if (features?.current_disk_used > 0) {
            setdataused(features?.current_disk_used);
            if (features?.disk_usage_limit) {
              let uag = 1000000000;
              if (features?.disk_usage_limit == '0') {
                uag = 1000000000;
              } else if (features?.disk_usage_limit == '1') {
                uag = 5000000000;
              } else if (features?.disk_usage_limit == '2') {
                uag = 10000000000;
              }

              setTotalSpace(uag);
              setData([
                { name: 'Used', value: features?.current_disk_used },
                { name: 'Free', value: uag - features?.current_disk_used },
              ]);
            }
          }
        }
      });
    };
    getUserData();
    return () => {
      setdataused(0);
      setTotalSpace(1000000000);
      setData([
        { name: 'Used', value: 0 },
        { name: 'Free', value: 1000000000 },
      ]);
    };
  }, []);

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return <AdminPanelSettings />;
      case 'Admin':
        return <VerifiedUser />;
      case 'User':
        return <LockOpen />;
      default:
        return <VerifiedUser />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return theme.palette.error.main;
      case 'Admin':
        return theme.palette.primary.main;
      case 'User':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const usagePercentage = ((dataused / totalSpace) * 100).toFixed(1);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          },
        },
      }}
    >
      {/* User Role Section */}
      <Box sx={{ mb: 1.6 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
            letterSpacing={0.8}
            sx={{ fontSize: '0.6rem' }}
          >
            {' '}
            {/* 1*0.8, scaled font */}
            Account Info
          </Typography>
          <Chip
            icon={getRoleIcon(props.role)}
            label={props.role}
            sx={{
              fontWeight: 700,
              fontSize: '0.68rem',
              height: 25.6,
              backgroundColor: alpha(getRoleColor(props.role), 0.1),
              color: getRoleColor(props.role),
              border: `1.6px solid ${alpha(getRoleColor(props.role), 0.3)}`,
              '& .MuiChip-icon': {
                color: getRoleColor(props.role),
                fontSize: '1rem', // Scaled icon
              },
            }}
          />
        </Box>

        {props.role === 'User' && (
          <Box
            sx={{
              p: 1.2,
              borderRadius: 1.6,
              backgroundColor: props.fullAccess
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.warning.main, 0.1),
              border: `0.8px solid ${
                props.fullAccess
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.warning.main, 0.3)
              }`,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: '0.7rem' }} // Scaled
              color={props.fullAccess ? 'success.main' : 'warning.main'}
            >
              {props.fullAccess ? '✓ Full Access Granted' : '⚠ Limited Access'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Cloud Storage Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.6 }}>
          {' '}
          {/* 1*0.8, 2*0.8 */}
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.3)}`, // 0.8
            }}
          >
            <CloudQueue sx={{ color: 'white', fontSize: 17.6 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
              Cloud Storage
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              {formatBytes(dataused)} / {formatBytes(totalSpace)}
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            my: 1.2,
          }}
        >
          <ReactApexChart options={ss.options} series={ss.series} type="donut" width={160} />
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mt: 1.2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ fontSize: '0.7rem' }}
            >
              Usage
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color="primary"
              sx={{ fontSize: '0.7rem' }}
            >
              {usagePercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={parseFloat(usagePercentage)}
            sx={{
              height: 6.4,
              borderRadius: 3.2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3.2,
                background:
                  theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              0
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              {formatBytes(totalSpace)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
